import Ionicons from "@expo/vector-icons/Ionicons";
import { Platform, Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { ThemedText } from "@/components/themed-text";
import { Avatar } from "@/components/ui/avatar";
import { Palette, Spacing } from "@/constants/theme";
import { FRIENDSHIP_GOALS } from "@/constants/options";
import { useTheme } from "@/hooks/use-theme";
import { homeStrings } from "@/i18n/features/home.i18n";
import type { NearbyUser } from "@/types";

export function NearbyRow({
  user,
  sent,
  onSendCue,
  onOpenProfile,
  compact = false,
}: {
  user: NearbyUser;
  sent: boolean;
  onSendCue: () => void;
  onOpenProfile?: () => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const c = useTheme();
  const interests = user.interests?.filter(Boolean).slice(0, 3) ?? [];
  const primaryGoal = user.lookingFor?.find(Boolean);
  const primaryGoalLabel =
    FRIENDSHIP_GOALS.find((goal) => goal.value === primaryGoal)?.label ??
    primaryGoal;
  const headline = user.headline?.trim();

  return (
    <View
      style={[
        styles.row,
        compact && styles.compactRow,
        { borderBottomColor: c.border },
      ]}
    >
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(homeStrings.openProfileA11y, {
          name: user.displayName,
        })}
        onPress={onOpenProfile}
        disabled={!onOpenProfile}
        style={({ hovered, pressed }) => [
          styles.profileTap,
          Platform.OS === "web" && hovered && { backgroundColor: c.surfaceAlt },
          pressed && onOpenProfile && styles.pressed,
        ]}
      >
        <Avatar
          seed={user.avatarSeed}
          avatarConfig={user.avatarConfig}
          name={user.displayName}
          size={50}
          emoji={user.vibe?.emoji}
        />

        <View style={styles.info}>
          <View style={styles.nameRow}>
            {/*
              Name, badge and venue tag are three elements rather than one
              string so the check sits against the name it vouches for. Folded
              into the old single Text, it would have landed after the venue
              tag and read as a claim about the place.

              Both texts shrink; the badge does not. A long name gives up
              characters before the badge gives up existing, which is the right
              trade for the one mark a stranger is asked to trust.
            */}
            <ThemedText
              variant="bodyStrong"
              style={styles.name}
              numberOfLines={1}
            >
              {user.displayName}
            </ThemedText>
            {user.venueTag ? (
              <ThemedText
                variant="bodyStrong"
                style={styles.venueTag}
                numberOfLines={1}
              >
                {`· ${user.venueTag}`}
              </ThemedText>
            ) : null}
          </View>

          {user.ageBand ? (
            <ThemedText variant="caption" tone="muted">
              {user.ageBand}
            </ThemedText>
          ) : null}

          {headline ? (
            <ThemedText variant="caption" numberOfLines={2}>
              {headline}
            </ThemedText>
          ) : null}

          {interests.length ? (
            <ThemedText variant="caption" tone="muted" numberOfLines={1}>
              {interests.join(" · ")}
            </ThemedText>
          ) : null}

          {primaryGoalLabel ? (
            <ThemedText variant="caption" tone="muted" numberOfLines={1}>
              {t(homeStrings.hereFor, { goal: primaryGoalLabel })}
            </ThemedText>
          ) : null}
        </View>
      </Pressable>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(
          sent ? homeStrings.cueSentA11y : homeStrings.sendCueA11y,
          { name: user.displayName },
        )}
        disabled={sent}
        onPress={onSendCue}
        style={({ pressed }) => [
          styles.cueAction,
          {
            borderColor: sent ? c.border : Palette.blue,
            backgroundColor: sent ? c.surfaceAlt : Palette.blue,
          },
          pressed && !sent && styles.cueActionPressed,
          sent && styles.cueActionDisabled,
        ]}
      >
        <Ionicons
          name={sent ? "checkmark" : "paper-plane-outline"}
          size={15}
          color={sent ? c.textMuted : Palette.white}
        />
        <Text
          style={[
            styles.cueActionText,
            { color: sent ? c.textMuted : Palette.white },
          ]}
        >
          {t(sent ? homeStrings.sent : homeStrings.cue)}
        </Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    minHeight: 82,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    backgroundColor: "transparent",
  },
  compactRow: {
    minHeight: 82,
  },
  profileTap: {
    flex: 1,
    minWidth: 0,
    minHeight: 72,
    paddingHorizontal: Spacing.sm,
    paddingVertical: Spacing.sm,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    borderRadius: 12,
  },
  pressed: { opacity: 0.82 },
  info: { flex: 1, minWidth: 0, gap: 2 },
  nameRow: {
    flexDirection: "row",
    alignItems: "center",
    // `xs`, not `sm`: this gap used to fall between a single child and nothing.
    // With three children it is real spacing, and `sm` pushed the check far
    // enough from the name to read as a separate item in the row.
    gap: Spacing.xs,
  },
  // `flexShrink`, not `flex: 1` — the name should take the width it needs and
  // give ground only when the row runs out, rather than always filling and
  // shoving the badge to the far edge.
  name: { flexShrink: 1 },
  venueTag: { flexShrink: 1 },
  cueAction: {
    minHeight: 35,
    minWidth: 68,
    paddingHorizontal: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    borderRadius: 18,
    borderWidth: 1,
  },
  cueActionPressed: {
    opacity: 0.82,
    transform: [{ scale: 0.98 }],
  },
  cueActionDisabled: {
    opacity: 0.72,
  },
  cueActionText: {
    fontSize: 12,
    fontWeight: "700",
  },
});
